<template>
  <td class="SectionProgress">
    <div class="cells">
      <ProgressCell
        v-for="cell in cells"
        :key="cell.key"
        :icon="cell.icon"
        :imageFileId="cell.imageFileId"
        :isClickable="cell.isClickable"
        :isLoading="cell.isLoading"
        :label="cell.icon ? null : cell.label"
        :subIcon="cell.subIcon"
        :theme="cell.theme"
        @click="cell.onClick" />
    </div>
    <ModalImageSelect
      v-if="doShowImageModal"
      :article="article"
      :fileId="section.imageFileId"
      :isUploading.sync="isPatching"
      :section="section"
      type="ARTICLE_SECTION_IMAGE"
      @close="doShowImageModal = false"
      @update:fields="patchSection" />
  </td>
</template>

<script>
import { actions, getters } from '@utils'
import { both, filter, chain as flatMap, includes, pathOr, pipe, propEq, propSatisfies } from 'rambda'
import ModalImageSelect from '@forms/ModalImageSelect'
import ProgressCell from './ProgressCell'
import { isNotNil } from '@utils/guards'
import sift from 'sift'

export default {
  components: {
    ModalImageSelect,
    ProgressCell,
  },
  props: {
    article: Object,
    sectionTemplateId: Number,
  },
  data() {
    return {
      doShowImageModal: false,
      isPatching: false,
    }
  },
  computed: {
    articleBlueprints() {
      return this
        .query('articleBlueprint', { articleId: this.article.id })
    },
    cells() {
      const { articleBlueprints, facts, isPatching, section, sectionTemplateId } = this

      const statusAttributes = statusAttributesFactory(
        articleBlueprints,
        sectionTemplateId,
      )

      if (!section) {
        return [
          {
            icon: 'text',
            theme: 'neutral',
            ...statusAttributes(
              false,
              propEq('sectionTemplateId', sectionTemplateId),
            ),
            onClick: () => this.createSection(),
          },
        ]
      }

      const { imageFileId, shouldApprove } = section
      const isApproved = !!section.approvedAt
      const hasVariant = (section?.textVariants || []).some(Boolean)

      const hasFactType = type => propSatisfies(
        both(isNotNil, includes(type)),
        'factsToGenerate',
      )

      const statusAttributesForFact = (factType) => {
        const fact = facts.find(propEq('type', factType))
        const hasItems = !!fact && !!fact.items.length
        const isLiked = hasItems && !!fact.likedAt

        return {
          subIcon: isLiked ? 'star' : null,
          ...statusAttributes(
            hasItems,
            hasFactType(factType),
            hasItems && !isApproved,
          ),
        }
      }

      return [
        {
          icon: 'images',
          // imageFileId,
          label: this.$t('image'),
          theme: statusTheme(
            false,
            false,
            false,
            !!imageFileId,
          ),
          onClick: () => {
            this.doShowImageModal = true
          },
        },
        {
          icon: shouldApprove ? 'lock' : 'unlock',
          label: this.$t('lock'),
          isLoading: isPatching,
          theme: statusTheme(
            isPatching,
            false,
            !isApproved && shouldApprove,
            isApproved,
          ),
          onClick: () => this.rotateApproval(),
        },
        ...this
          .sectionTemplate
          .factsTemplates
          .map(({ type }) => ({
            label: type.slice(0, 1),
            onClick: () => this.generateFacts(type),
            ...statusAttributesForFact(type),
          })),
        {
          label: this.$t('variant'),
          icon: 'text-fields',
          onClick: () => this.generateVariant(),
          ...statusAttributes(
            hasVariant,
            propEq('doCreateVariant', true),
            hasVariant && !isApproved,
          ),
        },
      ]
    },
    facts() {
      if (!this.section) return []

      return this
        .factsBySectionId[this.section.id] || []
    },
    section() {
      const { article, sectionTemplateId } = this

      if (!article || !article.id || !sectionTemplateId) return

      return this.queryOne('section', {
        articleId: article.id,
        sectionTemplateId,
      })
    },
    sectionTemplate() {
      return this
        .queryOne('sectionTemplate', this.sectionTemplateId)
    },
    ...getters('factsBySectionId'),
  },
  methods: {
    createSection() {
      return this.createArticleBlueprintSection({})
    },
    createArticleBlueprintSection(section) {
      return this.CREATE_ARTICLE_BLUEPRINT({
        articleId: this.article.id,
        siteId: this.article.siteId,
        scheme: {
          sections: [{
            sectionTemplateId: this.sectionTemplateId,
            ...section,
          }],
        },
      })
    },
    generateFacts(factType) {
      return this.createArticleBlueprintSection({
        factsToGenerate: [factType],
      })
    },
    generateVariant() {
      return this.createArticleBlueprintSection({
        doCreateVariant: true,
      })
    },
    rotateApproval() {
      const data = (() => {
        if (this.section.approvedAt) {
          return {
            approvedAt: null,
            shouldApprove: false,
          }
        }

        if (this.section.shouldApprove) {
          return {
            approvedAt: new Date(),
          }
        }

        return {
          shouldApprove: true,
        }
      })()

      return this.patchSection(data)
    },
    patchSection(data) {
      return this.$wrap(
        () => this.PATCH_SECTION({
          id: this.section.id,
          ...data,
        }),
        'isPatching',
      )
    },
    ...actions(
      'CREATE_ARTICLE_BLUEPRINT',
      'PATCH_SECTION',
    ),
  },
}

const statusTheme = (hasPending, hasFailed, hasWarning, doesExist) => {
  if (hasPending) return 'info'
  if (hasWarning) return 'warning'
  if (hasFailed) return 'danger'

  return doesExist
    ? 'success'
    : 'neutral'
}

const statusAttributesFactory = (articleBlueprints, sectionTemplateId) => {
  const hasSectionBlueprint = (articleQuery) => {
    const sectionBlueprints = pipe(
      filter(sift(articleQuery)),
      flatMap(pathOr([], ['scheme', 'sections'])),
      filter(propEq('sectionTemplateId', sectionTemplateId)),
    )(articleBlueprints)

    return condition => sectionBlueprints.some(condition)
  }

  const hasProcessing = hasSectionBlueprint({
    isProcessing: true,
  })

  const hasPending = hasSectionBlueprint({
    isProcessing: false,
    startedAt: null,
  })

  const queryFailed = {
    isProcessing: false,
    failedAt: { $ne: null },
  }

  const hasFailed = hasSectionBlueprint(queryFailed)

  return (doesExist, sectionBlueprintCondition, hasWarning = false) => ({
    isLoading: hasProcessing(sectionBlueprintCondition),
    theme: statusTheme(
      hasPending(sectionBlueprintCondition),
      hasFailed(sectionBlueprintCondition),
      hasWarning,
      doesExist,
    ),
  })
}
</script>

<style lang="scss">
.SectionProgress {
  background: $neutral-light;
  border-radius: $radius;
  overflow: hidden;

  > .cells {
    display: flex;
    width: 100%;
  }
}
</style>

<i18n>
{
  "en": {
    "image": "I",
    "lock": "L",
    "keyPoints": "K",
    "outline": "O",
    "variant": "V"
  }
}
</i18n>
